import { useEffect, useId, useRef, useState } from 'react';
import { HexColorInput, HexColorPicker } from 'react-colorful';

import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';

const PRESETS = [
  '#000000',
  '#ffffff',
  '#1e293b',
  '#dc2626',
  '#ea580c',
  '#ca8a04',
  '#16a34a',
  '#0891b2',
  '#2563eb',
  '#7c3aed',
  '#db2777',
  '#78350f',
] as const;

export interface ColorPickerProps {
  readonly label: string;
  readonly value: string;
  /** Receives the new hex colour directly — no synthetic event wrapper. */
  readonly onChange: (color: string) => void;
  readonly disabled?: boolean;
  readonly className?: string;
}

/**
 * Colour picker built on `react-colorful`.
 *
 * Replaces `react-color`, which was last released in 2020, declared
 * `peerDependencies: { react: "*" }` while the app runs React 19, and pulled
 * both `lodash` and `lodash-es` — the only two high-severity advisories in the
 * production dependency tree — plus ~19 MB of icon packages.
 */
export function ColorPicker({
  label,
  value,
  onChange,
  disabled = false,
  className,
}: ColorPickerProps) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputId = useId();

  useEffect(() => {
    if (!open) return;

    const onPointerDown = (event: PointerEvent): void => {
      const container = containerRef.current;
      if (container !== null && event.target instanceof Node && !container.contains(event.target)) {
        setOpen(false);
      }
    };
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') setOpen(false);
    };

    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  return (
    <div ref={containerRef} className={cn('relative space-y-2', className)}>
      <Label htmlFor={inputId}>{label}</Label>

      <button
        id={inputId}
        type="button"
        disabled={disabled}
        aria-expanded={open}
        aria-haspopup="dialog"
        onClick={() => {
          setOpen((previous) => !previous);
        }}
        className="flex h-9 w-full items-center gap-2 rounded-md border border-input bg-background px-3 text-sm shadow-sm transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
      >
        <span
          aria-hidden="true"
          className="h-5 w-5 shrink-0 rounded border border-border"
          style={{ backgroundColor: value }}
        />
        <span className="font-mono uppercase">{value}</span>
      </button>

      {open && (
        <div
          role="dialog"
          aria-label={`${label} auswählen`}
          className="absolute left-0 top-full z-30 mt-2 w-56 space-y-3 rounded-lg border bg-popover p-3 shadow-lg"
        >
          <HexColorPicker color={value} onChange={onChange} className="!w-full" />

          <HexColorInput
            color={value}
            onChange={onChange}
            prefixed
            aria-label="Hex-Farbwert"
            className="flex h-8 w-full rounded-md border border-input bg-transparent px-2 text-center font-mono text-sm uppercase focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          />

          <div className="grid grid-cols-6 gap-1.5">
            {PRESETS.map((preset) => (
              <button
                key={preset}
                type="button"
                title={preset}
                aria-label={preset}
                onClick={() => {
                  onChange(preset);
                }}
                className="h-6 w-full rounded border border-border transition-transform hover:scale-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                style={{ backgroundColor: preset }}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
